<template>
  <ion-modal
    :is-open="showPromo"
    css-class="install-modal"
    :backdrop-dismiss="!pending"
    @did-dismiss="close"
  >
    <InstallAppModal
      :mode="installMode"
      :pending="pending"
      :show-opt-out="true"
      @install="onInstall"
      @cancel="close"
      @optout="onOptOut"
    />
  </ion-modal>
</template>

<script setup lang="ts">
/**
 * The daily "Install Syntry" promotion.
 *
 * Appears at most ONCE PER CALENDAR DAY (local date), on the first
 * authenticated app load of the day. The day is stamped BEFORE the modal is
 * shown — if the stamp can't be persisted (private mode), the modal is
 * skipped rather than risk nagging on every load. Dismissing ("Not now" or
 * backdrop) simply ends today's ask; "Don't ask me again" — or an actual
 * install — stops it for good. Unlike the survey prompt, whose pacing the
 * backend owns and which therefore blocks backdrop dismissal, this is a
 * suggestion: it must be trivially escapable.
 *
 * The always-available entry point is "Install app" in the app drawer.
 */
import { ref, watch } from 'vue';
import { IonModal } from '@ionic/vue';
import { useRoute } from 'vue-router';
import InstallAppModal from '@/components/modals/InstallAppModal.vue';
import {
  installMode,
  installPromoEligible,
  markInstallPromoDone,
  markInstallPromptShown,
  promptInstall,
  recordAuthedSession,
} from '@/pwa/install';
import { updateAvailable } from '@/pwa/version-check';
import { useToast } from '@/composables/useToast';
import { useSessionStore } from '@/stores/session.store';
import { useSurveysStore } from '@/stores/surveys.store';
import { isPublicRouteName } from '@/router';

/** Let the page settle (and the survey fetch resolve) before interrupting. */
const SETTLE_MS = 2500;

const sessionStore = useSessionStore();
const surveysStore = useSurveysStore();
const route = useRoute();
const toast = useToast();

const showPromo = ref(false);
const pending = ref(false);

// Count the session once per app load. `immediate` with a falsy previous value
// also covers a session restored from storage at boot.
watch(
  () => sessionStore.isAuthenticated,
  (authed, was) => {
    if (authed && !was) recordAuthedSession();
  },
  { immediate: true },
);

watch(
  [installPromoEligible, () => sessionStore.isAuthenticated],
  ([eligible, authed]) => {
    if (!eligible || !authed || showPromo.value) return;
    window.setTimeout(() => {
      // Re-check on the way out of the delay — the user may have signed out,
      // navigated to a public route, or been handed a required survey.
      if (
        installPromoEligible.value &&
        sessionStore.isAuthenticated &&
        !isPublicRouteName(route.name) &&
        !surveysStore.current &&
        // Don't promote installing a build we're about to replace. The promo is
        // not spent by this — today's stamp is only written below — so it simply
        // waits for a later session.
        !updateAvailable.value
      ) {
        // Stamp today BEFORE showing; if we can't remember having asked,
        // don't ask (it would repeat on every load).
        if (!markInstallPromptShown()) return;
        showPromo.value = true;
      }
    }, SETTLE_MS);
  },
  { immediate: true },
);

/** Dismissal ends today's ask; tomorrow is a new day. */
function close(): void {
  showPromo.value = false;
}

/** Permanent opt-out — the drawer's "Install app" entry remains available. */
async function onOptOut(): Promise<void> {
  markInstallPromoDone();
  close();
  await toast.show("Okay — we won't ask again. You can still install Syntry from the menu.");
}

async function onInstall(): Promise<void> {
  if (pending.value) return;
  pending.value = true;
  try {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      await toast.success('Installing Syntry — check your home screen.');
    }
  } finally {
    pending.value = false;
    close();
  }
}
</script>
